import { useCallback, useState } from "react";
import { ChatMessage, RetrievedChunk } from "../types/chatbot";
import { streamMessage } from "../services/chatbotService";

const makeId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const updateMessage = (
  id: string,
  updater: (msg: ChatMessage) => ChatMessage
) => (prev: ChatMessage[]) =>
  prev.map((msg) => (msg.id === id ? updater(msg) : msg));

export const useChatSession = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || pending) return;

      const userMessage: ChatMessage = {
        id: makeId(),
        role: "user",
        content: trimmed,
      };
      const assistantId = makeId();
      const assistantPlaceholder: ChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        thinking: { retrieved: [], reasoning: [] },
      };

      const history = [...messages, userMessage];
      setMessages((prev) => [...prev, userMessage, assistantPlaceholder]);
      setPending(true);
      setError(null);

      let retrieved: RetrievedChunk[] = [];
      let reasoning: string[] = [];
      let streamErrorMessage: string | null = null;

      try {
        await streamMessage(history, trimmed, (event) => {
          switch (event.type) {
            case "retrieved":
              retrieved = event.retrieved;
              setMessages(
                updateMessage(assistantId, (msg) => ({
                  ...msg,
                  thinking: { retrieved, reasoning: msg.thinking?.reasoning ?? [] },
                }))
              );
              break;
            case "token":
              setMessages(
                updateMessage(assistantId, (msg) => ({
                  ...msg,
                  content: msg.content + event.delta,
                }))
              );
              break;
            case "reasoning":
              reasoning = event.reasoning;
              setMessages(
                updateMessage(assistantId, (msg) => ({
                  ...msg,
                  thinking: { retrieved, reasoning },
                }))
              );
              break;
            case "error":
              streamErrorMessage = event.message;
              break;
            case "done":
              break;
          }
        });

        if (streamErrorMessage) {
          throw new Error(streamErrorMessage);
        }
      } catch (err) {
        console.error("Chat send failed", err);
        setError(err instanceof Error ? err.message : "Failed to get a reply.");
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== assistantId || msg.content.length > 0)
        );
      } finally {
        setPending(false);
      }
    },
    [messages, pending]
  );

  const reset = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, pending, error, send, reset };
};
